import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';

import { ProseMirror } from 'prosemirror/edit';
import { menuBar } from 'prosemirror/menu';
import MENU_CONFIGS from './menu';
import { Node } from 'prosemirror/model';
import { buildKeymap } from 'prosemirror/example-setup/keymap';
import { setMathJaxTypeset } from 'chameleon/src/utility/utils';
import debounce from 'lodash/debounce';

import schema from './schema';
import migrateNode from './migrate';

import './ProseMirror.scss';

function prepareDoc(value) {
  if (typeof value === 'string' && this) {
    this.pm.setContent(value, 'html');
  }
  const json = _.isObject(value) ? migrateNode(value) : emptyDoc(value);
  return Node.fromJSON(schema, json);
}

function emptyDoc(text) {
  const textBlock = text ? { content: [{ type: 'text', text }] } : '';
  return { type: 'doc', content: [{ type: 'paragraph', ...textBlock }] };
}

const Prose = ({ menu, valueLink, options = {} }) => {
  const [pm, setPm] = useState(null);
  const [value, setValue] = useState(valueLink.value);
  const [lastValue, setLastValue] = useState(valueLink.value);
  const [modal, setModal] = useState(null);
  const ref = useRef(null);

  const setMath = debounce(() => {
    ref.current && setMathJaxTypeset(ref.current);
  }, 500);

  const setDebouncedMath = () => {
    setMath.cancel();
    setMath();
  };

  useEffect(() => {
    const proseMirror = new ProseMirror({
      doc: prepareDoc(valueLink.value),
      docFormat: 'json',
      autoInput: true,
      itemEmbeds: true,
      schema,
      plugins: [
        menuBar.config({
          content: MENU_CONFIGS[menu || 'default'],
        }),
      ],
      ...options,
    });

    proseMirror.addKeymap(buildKeymap(schema));

    window.pm = proseMirror;

    proseMirror.requestNodeModal = (component, props) => {
      setModal({
        component,
        props: {
          ...props,
          close: () => {
            setModal(null);
            /**
             * When we embed math, we want the math to be propertly formatted.
             * When the embed modal closes seems like the best way to do this.
             */
            setDebouncedMath();
          },
        },
      });
    };

    /**
     * Triggered when the component initially mounts,
     * we want the math to be propertly formatted
     */
    proseMirror.on.setDoc.add(setDebouncedMath);
    setPm(proseMirror);

    return () => {
      proseMirror.on.setDoc.remove(setDebouncedMath);
    };
  }, []);

  useEffect(() => {
    if (pm) {
      ref.current.appendChild(pm.wrapper);
      setDebouncedMath();
    }
  }, [pm]);

  useEffect(() => {
    if (pm && valueLink) {
      const updatePMContent = _.throttle(() => {
        const _lastValue = pm.doc.toJSON();
        setLastValue(_lastValue);
        valueLink.requestChange(_lastValue);
      }, 250);

      pm.on.change.add(updatePMContent);

      return () => pm.on.change.remove(updatePMContent);
    }
  }, [pm, valueLink]);

  useEffect(() => {
    const newValue = valueLink.value;

    if (pm) {
      if (newValue !== value && newValue !== lastValue) {
        setValue(newValue);
        setLastValue(newValue);
        pm.setDoc(prepareDoc(newValue));
      }
    }
  }, [valueLink.value]);

  return (
    <div
      className={`ProseMirror-component l ${options.hasError ? 'error' : ''}`}
    >
      {modal && (
        <div className="modal">
          <modal.component {...modal.props} />
        </div>
      )}
      <div ref={ref} />
    </div>
  );
};

Prose.displayName = 'ProseMirrorN';

export default Prose;
